import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircleIcon,
  EnvelopeSimpleIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";

const CATEGORIES = [
  {
    key: "transactional",
    label: "Order & account emails",
    description:
      "Order receipts, shipping updates, payouts, password resets, and verification decisions. Required — can't be disabled.",
    locked: true,
  },
  {
    key: "engagement",
    label: "Activity alerts",
    description:
      "Outbid notices, stream-going-live alerts, and new-follower updates. Delivered only when push isn't available.",
    locked: false,
  },
  {
    key: "marketing",
    label: "Drops & promotions",
    description:
      "Weekly featured drops, new category launches, and Founder Seller offers. Turn off anytime.",
    locked: false,
  },
];

export default function EmailPreferences() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const autoUnsubscribe = searchParams.get("action") === "unsubscribe";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [justUnsubscribed, setJustUnsubscribed] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/email/preferences/${token}`);
        if (!active) return;
        setEmail(data.email || "");
        setPrefs(data.preferences);
        if (autoUnsubscribe) {
          await api.post(`/email/preferences/${token}`, {
            marketing: false,
            engagement: false,
          });
          setPrefs({
            transactional: true,
            marketing: false,
            engagement: false,
          });
          setJustUnsubscribed(true);
        }
      } catch (err) {
        if (!active) return;
        setError(apiError(err) || "This link is invalid or has expired.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const toggle = async (key, next) => {
    if (!prefs) return;
    const prev = prefs;
    const optimistic = { ...prefs, [key]: next };
    setPrefs(optimistic);
    setSaving(true);
    try {
      const { data } = await api.post(`/email/preferences/${token}`, {
        [key]: next,
      });
      setPrefs(data.preferences);
      toast.success("Preferences saved");
    } catch (err) {
      setPrefs(prev);
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const unsubscribeAll = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/email/preferences/${token}`, {
        marketing: false,
        engagement: false,
      });
      setPrefs(data.preferences);
      setJustUnsubscribed(true);
      toast.success("You're unsubscribed from non-essential emails.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-white flex flex-col"
      data-testid="email-preferences-page"
    >
      <Navbar />
      <main className="flex-1 max-w-xl mx-auto w-full px-6 py-16">
        {loading ? (
          <div className="text-center py-20 text-zinc-500" data-testid="email-prefs-loading">
            Loading your preferences…
          </div>
        ) : error ? (
          <div className="text-center" data-testid="email-prefs-error">
            <div className="h-14 w-14 mx-auto bg-red-100 text-red-700 rounded-full grid place-items-center mb-4">
              <WarningCircleIcon size={28} weight="fill" />
            </div>
            <h1 className="font-display font-black text-3xl tracking-tight">
              Link problem
            </h1>
            <p className="text-zinc-600 mt-3 text-sm leading-relaxed">{error}</p>
            <Link
              to="/"
              className="inline-block mt-6 text-[#1D78AB] font-semibold hover:underline"
              data-testid="email-prefs-home-link"
            >
              Back to Bidorah
            </Link>
          </div>
        ) : (
          <>
            {justUnsubscribed && (
              <div
                className="mb-6 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 flex gap-3 items-start"
                data-testid="email-prefs-unsub-banner"
              >
                <CheckCircleIcon
                  size={22}
                  weight="fill"
                  className="text-emerald-700 flex-shrink-0 mt-0.5"
                />
                <div className="text-sm text-emerald-900 leading-relaxed">
                  <div className="font-semibold">You're unsubscribed.</div>
                  You'll still get essential order and account emails — those
                  are legally required when you have active listings or
                  purchases on Bidorah.
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 mb-2">
              <div className="h-11 w-11 bg-[#1D78AB]/10 text-[#1D78AB] rounded-2xl grid place-items-center">
                <EnvelopeSimpleIcon size={22} weight="fill" />
              </div>
              <div>
                <h1 className="font-display font-black text-3xl tracking-tight leading-none">
                  Email preferences
                </h1>
                <p className="text-sm text-zinc-500 mt-1">{email}</p>
              </div>
            </div>

            <p className="text-sm text-zinc-600 mt-4">
              Choose what Bidorah is allowed to send you. Changes save
              automatically.
            </p>

            <div className="mt-8 space-y-3">
              {CATEGORIES.map((cat) => {
                const enabled = !!prefs?.[cat.key];
                return (
                  <div
                    key={cat.key}
                    className={`rounded-2xl border p-5 transition ${
                      enabled
                        ? "border-[#1D78AB]/30 bg-[#1D78AB]/5"
                        : "border-zinc-200 bg-white"
                    } ${cat.locked ? "opacity-90" : ""}`}
                    data-testid={`email-prefs-card-${cat.key}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-zinc-900">
                            {cat.label}
                          </div>
                          {cat.locked && (
                            <span
                              className="text-[10px] uppercase tracking-wider bg-zinc-900 text-white rounded-full px-2 py-0.5 font-semibold"
                              data-testid={`email-prefs-lock-${cat.key}`}
                            >
                              Required
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-zinc-600 mt-1.5 leading-relaxed">
                          {cat.description}
                        </p>
                      </div>
                      <Switch
                        checked={enabled}
                        disabled={cat.locked || saving}
                        onCheckedChange={(next) =>
                          !cat.locked && toggle(cat.key, next)
                        }
                        data-testid={`email-prefs-toggle-${cat.key}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 pt-6 border-t border-zinc-200 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <p className="text-xs text-zinc-500 leading-relaxed max-w-sm">
                Want nothing non-essential from us? One click unsubscribes you
                from both categories above.
              </p>
              <Button
                variant="outline"
                onClick={unsubscribeAll}
                disabled={
                  saving ||
                  (!prefs?.marketing && !prefs?.engagement)
                }
                className="border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                data-testid="email-prefs-unsub-all-btn"
              >
                Unsubscribe from all non-essential
              </Button>
            </div>

            <p className="text-xs text-zinc-400 mt-8 leading-relaxed">
              This link is tied to your email address and is valid for 365
              days. Bidorah, Inc. · bidorah.com
            </p>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
